/**
 * Phase 98 — Sport-Specific Progress Metrics Engine
 *
 * Replaces the legacy hardcoded football matrix with a context-aware
 * registry that picks the right evaluation skills based on the student's
 * enrolled sport / course name. New sports can be added by appending an
 * entry — no UI changes required.
 *
 * Matching is case-insensitive substring (so "Football Pro", "U-15 Football
 * Elite", and "Football" all resolve to the football matrix). The keys are
 * checked in priority order — list more specific sports first if any
 * overlap (e.g. "Mixed Cricket" vs "Cricket Bowling Bootcamp" both win
 * Cricket because Cricket is the only sport containing that substring).
 *
 * Skill IDs (snake_case) are what we persist to MongoDB; the `label` is
 * the human-readable name shown in the UI. Persisting by ID means we can
 * rename the label later without re-keying historical progress cards.
 */

const SPORT_MATRICES = [
  {
    sport: 'Hockey',
    aliases: ['field hockey'],
    skills: [
      { id: 'stickwork_control',        label: 'Stickwork / Control' },
      { id: 'push_hit_accuracy',        label: 'Push & Hit Accuracy' },
      { id: 'tackling_interceptions',   label: 'Tackling & Interceptions' },
      { id: 'tactical_positioning',     label: 'Tactical Positioning' },
      { id: 'speed_endurance',          label: 'Speed Endurance' },
    ],
  },
  {
    sport: 'Cricket',
    skills: [
      { id: 'batting_mechanics',  label: 'Batting Mechanics' },
      { id: 'bowling_accuracy',   label: 'Bowling Line / Length' },
      { id: 'fielding_agility',   label: 'Fielding & Agility' },
      { id: 'match_awareness',    label: 'Match Awareness' },
      { id: 'fitness',            label: 'Fitness' },
    ],
  },
  {
    sport: 'Tennis',
    skills: [
      { id: 'forehand',           label: 'Forehand Stroke' },
      { id: 'backhand',           label: 'Backhand Stroke' },
      { id: 'serve_accuracy',     label: 'Serve Accuracy' },
      { id: 'footwork',           label: 'Footwork' },
      { id: 'match_temperament',  label: 'Match Temperament' },
    ],
  },
  {
    sport: 'Badminton',
    skills: [
      { id: 'smash_power',        label: 'Smash Power' },
      { id: 'net_play',           label: 'Net Play' },
      { id: 'footwork',           label: 'Footwork' },
      { id: 'shot_variation',     label: 'Shot Variation' },
      { id: 'stamina',            label: 'Stamina' },
    ],
  },
  {
    sport: 'Basketball',
    skills: [
      { id: 'ball_handling',      label: 'Ball Handling' },
      { id: 'shooting',           label: 'Shooting' },
      { id: 'defense',            label: 'Defense' },
      { id: 'court_vision',       label: 'Court Vision' },
      { id: 'athleticism',        label: 'Athleticism' },
    ],
  },
  {
    sport: 'Swimming',
    skills: [
      { id: 'stroke_technique',   label: 'Stroke Technique' },
      { id: 'lap_endurance',      label: 'Lap Endurance' },
      { id: 'turns_and_dives',    label: 'Turns & Dives' },
      { id: 'breathing_rhythm',   label: 'Breathing Rhythm' },
      { id: 'race_pace',          label: 'Race Pace' },
    ],
  },
  {
    sport: 'Athletics',
    aliases: ['running', 'track', 'sprint'],
    skills: [
      { id: 'sprint_speed',       label: 'Sprint Speed' },
      { id: 'endurance',          label: 'Endurance' },
      { id: 'starting_form',      label: 'Starting Form' },
      { id: 'recovery',           label: 'Recovery Discipline' },
      { id: 'race_strategy',      label: 'Race Strategy' },
    ],
  },
  {
    sport: 'Football',
    aliases: ['soccer'],
    skills: [
      { id: 'dribbling',  label: 'Dribbling' },
      { id: 'passing',    label: 'Passing' },
      { id: 'shooting',   label: 'Shooting' },
      { id: 'fitness',    label: 'Fitness' },
      { id: 'teamwork',   label: 'Teamwork' },
    ],
  },
  {
    sport: 'Volleyball',
    skills: [
      { id: 'serve_accuracy',     label: 'Serve Accuracy' },
      { id: 'spike_power',        label: 'Spike Power' },
      { id: 'blocking',           label: 'Blocking' },
      { id: 'court_positioning',  label: 'Court Positioning' },
      { id: 'reflex_dives',       label: 'Reflex & Dives' },
    ],
  },
  {
    sport: 'Boxing',
    aliases: ['kickboxing'],
    skills: [
      { id: 'jab_cross_combos',   label: 'Jab / Cross Combos' },
      { id: 'footwork',           label: 'Footwork' },
      { id: 'defense_slipping',   label: 'Defense & Slipping' },
      { id: 'power_stamina',      label: 'Power Stamina' },
      { id: 'ring_iq',            label: 'Ring IQ' },
    ],
  },
  {
    sport: 'Kabaddi',
    skills: [
      { id: 'raid_technique',     label: 'Raid Technique' },
      { id: 'defense_holds',      label: 'Defense Holds' },
      { id: 'breath_control',     label: 'Breath Control' },
      { id: 'tactical_calls',     label: 'Tactical Calls' },
      { id: 'agility_explosive',  label: 'Agility & Explosive Power' },
    ],
  },
];

// Generic fallback when the sport is missing/unknown. Keeps the form usable
// for academies running niche sports while still giving structured analytics.
const FALLBACK_MATRIX = {
  sport: 'General',
  isFallback: true,
  skills: [
    { id: 'technique',     label: 'Technique' },
    { id: 'athleticism',   label: 'Athleticism' },
    { id: 'discipline',    label: 'Discipline' },
    { id: 'teamwork',      label: 'Teamwork / Coachability' },
    { id: 'game_sense',    label: 'Game Sense' },
  ],
};

/**
 * Flat list of all sports the engine supports. Used by the manual sport
 * override dropdown in the progress-card modal.
 */
export const AVAILABLE_SPORTS_LIST = SPORT_MATRICES.map((m) => m.sport);

/**
 * Resolve the metrics matrix for a sport / course label.
 * @param {string} sportOrCourse — case-insensitive, may include extra words
 *   like "U-15 Hockey Evening Batch". First substring match wins.
 * @returns {{sport: string, skills: Array<{id: string, label: string}>}}
 */
export function getSportMatrix(sportOrCourse) {
  if (!sportOrCourse || typeof sportOrCourse !== 'string') return FALLBACK_MATRIX;
  const haystack = sportOrCourse.toLowerCase();
  for (const m of SPORT_MATRICES) {
    if (haystack.includes(m.sport.toLowerCase())) return m;
    if (m.aliases) {
      for (const a of m.aliases) {
        if (haystack.includes(a.toLowerCase())) return m;
      }
    }
  }
  return FALLBACK_MATRIX;
}

/**
 * Convenience: return just the IDs for the matrix. Stable across re-renders.
 */
export function getSportSkillIds(sportOrCourse) {
  return getSportMatrix(sportOrCourse).skills.map((s) => s.id);
}

/**
 * Convenience for legacy callers that need just the labels (e.g. old Students.js
 * Add Progress dialog used plain strings as keys).
 */
export function getSportSkillLabels(sportOrCourse) {
  return getSportMatrix(sportOrCourse).skills.map((s) => s.label);
}
